import { Domain } from "~/api/Api";

import {
  DnsModelInput,
  DnsPayloadRow,
  DnsRecordData,
  DnsRecordModel,
  ModalInitialData,
} from "./types";

export const DEFAULT_SUBDOMAIN = "@";
export const DEFAULT_TTL = 3600;

export const ns = [
  {
    title: "Nameserver 1",
    value: "ns1.alliaidns.com",
  },
  {
    title: "Nameserver 2",
    value: "ns2.alliaidns.com",
  },
  {
    title: "Nameserver 3",
    value: "ns3.alliaidns.com",
  },
  {
    title: "Nameserver 4",
    value: "ns4.alliaidns.com",
  },
];

export const partialDnsRecordObjectTypes = [
  {
    value: "A",
    label: "A - Routes traffic to an IPv4 address",
  },
  // {
  //   value: "AAAA",
  //   label: "AAAA - Routes traffic to an IPv6 address",
  // },
  {
    value: "CNAME",
    label: "CNAME - Routes traffic to another domain name",
  },
];
export const dnsRecordObjectTypes = [
  {
    value: "A",
    label: "A - Routes traffic to an IPv4 address",
  },
  {
    value: "AAAA",
    label: "AAAA - Routes traffic to an IPv6 address",
  },
  {
    value: "CNAME",
    label: "CNAME - Routes traffic to another domain name",
  },
  {
    value: "MX",
    label: "MX - Specifies mail servers",
  },
  {
    value: "TXT",
    label:
      "TXT - Used to verify email senders and for application-specific values",
  },
  {
    value: "PTR",
    label: "PTR - Maps an IP address to a domain name",
  },
  {
    value: "SRV",
    label: "SRV - Application-specific values that identify servers",
  },
  {
    value: "SPF",
    label: "SPF - Not recommended",
  },
  {
    value: "NAPTR",
    label: "NAPTR - Used by DDDS applications",
  },
  {
    value: "CAA",
    label:
      "CAA - Restricts CAs that can create SSL/TLS certificates for the domain",
  },
];

export const ttlOptions = [
  { value: 300, label: "5m" },
  { value: 600, label: "10m" },
  { value: DEFAULT_TTL, label: "1h" },
  { value: 86400, label: "1d" },
];

export const wizardStepsLabels = [
  {
    title: "Setup",
    id: "setup",
  },
  {
    title: "Verification",
    id: "verification",
  },
  {
    title: "Certificate",
    id: "certificate",
  },
  {
    title: "DNS",
    id: "dns",
  },
  {
    title: "Nameservers",
    id: "ns",
  },
  {
    title: "Routing",
    id: "routing",
  },
  {
    title: "Confirmation",
    id: "confirmation",
  },
  {
    title: "Install",
    id: "install",
  },
  {
    title: "Configure",
    id: "configure",
  },
];
export const routingConfig = [
  {
    type: "A",
    value: "52.223.43.88",
    ttl: "60",
  },
  {
    type: "A",
    value: "99.83.177.15",
    ttl: "60",
  },
];

export const POLLING_INTERVAL = 3000;

export const ensureTrailingDot = (s: string) =>
  s.length && !s.endsWith(".") ? `${s}.` : s;

/** Compose FQDN from sub/base. Accepts "@", "", and already-FQDN. */
export function composeFQDN(subdomain: string, baseDomain: string): string {
  const base = ensureTrailingDot(baseDomain || "");
  if (!subdomain || subdomain === DEFAULT_SUBDOMAIN) return base;

  return subdomain.endsWith(".") ? subdomain : `${subdomain}.${base}`;
}

/** Normalize arbitrary input name relative to baseDomain (to FQDN with trailing dot). */
export function normalizeNameToFQDN(input: string, baseDomain: string): string {
  const raw = String(input || "").replace(/@/g, "");
  if (!raw) return ensureTrailingDot(baseDomain || "");
  if (raw.endsWith(".")) return raw;

  return composeFQDN(raw, baseDomain);
}

export function serverDataToModels(
  rows: (string | number)[][],
  baseDomain: string,
  source: "api" | "import",
): DnsRecordModel[] {
  const base = ensureTrailingDot(baseDomain || "");
  const typed = rows as DnsPayloadRow[];
  const out: DnsRecordModel[] = [];

  for (let i = 0; i < typed.length; i++) {
    const [name, ttl, type, content] = typed[i];

    out.push({
      id: source === "api" ? i : -(Date.now() + i), // api: stable indexes; import: local negative ids
      type: String(type),
      nameFQDN: normalizeNameToFQDN(String(name), base),
      content: String(content).trim(),
      ttl: Number(ttl),
      action: source === "api" ? null : "add", // api = base (null), import = local ("add")
    });
  }

  return out;
}

export function apiDataToModels(
  rows: (string | number)[][],
  baseDomain: string,
): DnsRecordModel[] {
  return serverDataToModels(rows, baseDomain, "api");
}

export function apiDataToChangesMap(
  rows: (string | number)[][],
  baseDomain: string,
): Map<number, DnsRecordModel> {
  const models = serverDataToModels(rows, baseDomain, "import");
  const map = new Map<number, DnsRecordModel>();
  for (const m of models) {
    map.set(m.id, m);
  }

  return map;
}

/**
 * Merge base models with local changes and produce payload for user_config_pending.
 * - baseRecords: current records from API (normalized to models, action=null)
 * - changes:     local "add/update/delete" records keyed by id
 */
export function buildPendingPayload(
  baseRecords: DnsRecordModel[],
  changes: Map<number, DnsRecordModel>,
): DnsPayloadRow[] {
  // Make a shallow copy so we never mutate the input array.
  // (We will modify `result` freely below.)
  const result: DnsRecordModel[] = baseRecords.slice();

  // Build an index for quick lookup: record.id -> position in `result`.
  // Using a plain object to keep it readable.
  const indexById: Record<number, number> = {};
  for (let i = 0; i < result.length; i++) indexById[result[i].id] = i;

  // Apply all changes in one pass.
  for (const change of changes.values()) {
    if (change.action === "delete") {
      const pos = indexById[change.id];

      if (pos !== undefined) {
        // Remove one element at array index `pos`.
        result.splice(pos, 1);

        // After splice, items to the right shift left by 1.
        // Rebuild the index mapping for the affected tail so id -> index stays correct.
        for (let i = pos; i < result.length; i++) {
          indexById[result[i].id] = i;
        }
        // Also remove the deleted id from the index.
        delete indexById[change.id];
      }
    } else if (change.action === "update") {
      const pos = indexById[change.id];
      if (pos !== undefined) {
        // Overwrite fields but keep it a base record (action=null in final list).
        result[pos] = { ...result[pos], ...change, action: null };
      }
    } else if (change.action === "add") {
      // Append new local record as-is (it has a negative or temp id).
      result.push({ ...change });
    }
  }

  // Convert back to the API payload shape: [name, ttl, type, content]
  const rows: DnsPayloadRow[] = [];
  for (const r of result) rows.push([r.nameFQDN, r.ttl, r.type, r.content]);

  return rows;
}

export function dnsPayloadModel(input: DnsModelInput): DnsRecordModel {
  const { id, type, content, ttl, action = "delete", name } = input;

  return {
    id,
    type,
    nameFQDN: ensureTrailingDot(String(name).trim()),
    content: String(content).trim(),
    ttl: Number(ttl),
    action,
  };
}

export const isAdd = (
  r: DnsRecordModel,
): r is DnsRecordModel & { action: "add" } => r.action === "add";
export const isUpdate = (
  r: DnsRecordModel,
): r is DnsRecordModel & { action: "update" } => r.action === "update";
export const isDelete = (
  r: DnsRecordModel,
): r is DnsRecordModel & { action: "delete" } => r.action === "delete";

/** initialData для режима Add */
export function makeAddInitialData(): {
  id: number;
  type: string;
  name: string;
  content: string;
  ttl: number;
  action: "add";
} {
  return {
    id: Date.now(),
    type: "A",
    name: "",
    content: "",
    ttl: DEFAULT_TTL,
    action: "add",
  };
}

export function makeEditInitialData(
  row:
    | Pick<DnsRecordModel, "id" | "type" | "nameFQDN" | "content" | "ttl">
    | Pick<DnsRecordData, "id" | "type" | "nameFQDN" | "content" | "ttl">,
): ModalInitialData {
  return {
    id: row.id,
    type: row.type,
    name: row.nameFQDN, // pass FQDN — the modal will use extractSubdomainAndBaseAsync on it
    content: row.content,
    ttl: row.ttl,
    action: "update",
  };
}

export function isDomainPrerenderingStatusHealthy(domain: Domain) {
  const sites = domain.sites ?? [];
  const sitesWithPrerendering = sites.filter((s) => s.prerendering_enabled);

  return sitesWithPrerendering && sitesWithPrerendering.length > 0;
}

export function isDomainReady(domain: Domain) {
  return domain.status && Object.values(domain.status).every((v) => v === true);
}
